import { t as translateDate } from './translate-date.js';
import { U as Url } from './url.min.js';
import './index2.js';

/**
 * **Simplotel**
 *
 * @exports bs_31
 * @category engines
 */
function factory({ window, document, thn }) {

  const engine = {};

  /**
     * @memberOf module:bs_31
     * @see {@link prestart}
     *
     * @param {object} ctx - The context object.
     */
  engine.boot = function boot() {

    const container = document.body;

    if (!thn.simplotelPageObserver && container) {

      thn.simplotelPageObserver = thn.debounce(function (mutations) {

        const roomVisible = document.querySelectorAll('.room-selection-header:not(.collapsed)').length;

        if (roomVisible // make sure the room is still visible
          && !thn.empty(Array.from(mutations).filter((mutation) => (((mutation.addedNodes[0] || {}).className || '').match(/panel-(collapse|default)/) || [])[0]))) {
          thn.widgets.price.update();
        } else if (!thn.empty(Array.from(mutations).filter((mutation) => (((mutation.addedNodes[0] || {}).id || '').match(/workflow-step|demo/) || [])[0]))) {
          thn.pageCheck();
        }
      }, 500);

      thn.observeDom(container, thn.simplotelPageObserver, {
        childList: true,
        subtree: true,
        attributes: false,
      });

      thn.shutdown(function () {
        thn.stopObserveDom(container, thn.simplotelPageObserver);
        delete thn.simplotelPageObserver;
      });
    }
  };

  /**
     * @memberOf module:bs_31
     * @see {@link generate_search_url}
     *
     * @param {object} ctx - The context object.
     */
  engine.generateSearchUrl = function generateSearchUrl(ctx) {

    const url = new Url(ctx.baseUrl);

    if (url.query.propertyId) { // rooms and rates

      if (ctx.newData.dateBegin && ctx.newData.dateEnd) {

        ctx.newData.dateBegin = ctx.newData.dateBegin.replace(/\//g, '-');
        ctx.newData.dateEnd = ctx.newData.dateEnd.replace(/\//g, '-');

        url.query.checkIn = '[date_begin]';
        url.query.checkOut = '[date_end]';
      }

      if (ctx.newData.rooms) {
        url.query.rooms = '[rooms]';
      }

      url.query.adults = '[adults]';

      if (!thn.empty(ctx.newData.roomsDispo) && typeof ctx.newData.roomsDispo === 'object') {

        // set children
        url.query.children = '[children]';

        // for every room
        ctx.newData.roomsDispo.forEach((room, roomIdx) => {

          roomIdx = parseInt(roomIdx, 10) + 1;

          // set room adults
          url.query[`adults_${roomIdx}`] = room.adults;

          if (room.children) {
            // set room children
            url.query[`children_${roomIdx}`] = room.children;

            // for every children
            room.childrenAges.forEach((age, childIdx) => {
              childIdx = parseInt(childIdx, 10) + 1;

              // set children age
              url.query[`cage_${roomIdx}_${childIdx}`] = age;
            });
          }

        });
      }

      // force search (if false -> page stays in calendar)
      url.query.triggerSearch = 'true';

      ctx.newUrl = url.decode(url.toString());

    } else {
      ctx.newUrl = '';
    }

    return ctx;
  };

  /**
 * @memberOf module:bs_31
    * @this thn.widgets.price
    * @see {@link Price#observe_dom}
    *
    * @param {object} ctx - The context object.
    */
  engine.priceObserveDom = function priceObserveDom(ctx) {

    if (ctx.widget.isSet()) {
      ctx.widget.reset();
    }
  };

  /**
     * @memberOf module:bs_31
     * @this thn.widgets.price
     * @see {@link Price#set_data}
     *
     * @param {object} ctx - The context object.
     */
  engine.priceParseData = function priceParseData(ctx) {

    const widgetData = {};

    // Dates
    const searchedData = window.bookingData;

    const dateBeginText = document.querySelector('#checkInWrapper')?.textContent;
    const dateEndText = document.querySelector('#checkOutWrapper')?.textContent;

    // We can't use dates from url parameters because it doesn't update with new searches.
    if (searchedData.checkIn && searchedData.checkOut) {
      widgetData.dateBegin = thn.objectGet(searchedData, 'checkIn')?.replace(/-/g, '/');
      widgetData.dateEnd = thn.objectGet(searchedData, 'checkOut').replace(/-/g, '/');
    } else if (dateBeginText && dateEndText) {
      widgetData.dateBegin = translateDate(dateBeginText?.replace('\'', ''), thn.data.language);
      widgetData.dateEnd = translateDate(dateEndText?.replace('\'', ''), thn.data.language);
    }

    // Guests, Adults & Children

    // 'bit tricky to know which room we are viewing

    const rooms = Array.from(document.querySelectorAll('.room-selection-header')).map((room) => !room.className.match(/collapsed/));
    const currentRoom = rooms.indexOf(true) + 1;

    const guestsSelector = document.querySelector(`#roomContainer-${currentRoom}`);

    if (guestsSelector) {
      widgetData.adults = parseInt((guestsSelector.querySelector('.js-num-adults') || {}).value, 10) || 2;
      widgetData.children = parseInt((guestsSelector.querySelector('.js-num-children') || {}).value, 10) || 0;

      if (widgetData.children) {

        const room1Info = {
          adults: widgetData.adults,
          children: widgetData.children,
          childrenAges: [],
        };

        for (let i = 1; i <= widgetData.children; i += 1) {
          room1Info.childrenAges.push((guestsSelector.querySelector(`#child-age-${currentRoom}-${i}`) || {}).value || 10);
        }

        widgetData.roomsDispo = [room1Info];
      }
    }

    widgetData.adults = widgetData.adults || 2;
    widgetData.children = widgetData.children || 0;
    widgetData.guests = widgetData.adults + widgetData.children;

    const ratesBlacklist = /member-only/i;

    // Prices & Currency || No Rooms
    const prices = Array.from(document.querySelectorAll('.rate-plan-wrapper')) // Rooms
      .filter((el) => thn.isVisible(el) && !((el.querySelector('div.bold-font') || {}).textContent || '').match(ratesBlacklist)) // Filter room rates
      .map((el) => thn.parseNumber((el.querySelector('.discount-price') || {}).textContent)) // Get room prices
      .filter(Boolean); // Keep truthy values

    const currency = (((document.querySelector('.rate-block .per-night') || {}).textContent || '').match(/[A-Z]{3}/) || [])[0];

    if (prices.length && currency) {
      widgetData.price = Math.min(...prices);
      widgetData.currency = currency;
      widgetData.set = true;
    } else {
      widgetData.noRooms = true;
    }

    widgetData.set = true;

    return widgetData;
  };

  /**
     * @memberOf module:bs_31
     * @this thn.widgets.booking
     * @see {@link Booking#set_data}
     *
     * @param {object} ctx - The context object.
     */
  engine.bookingParseData = function bookingParseData(ctx) {

    const widgetData = {};

    const bookingData = window.booking;

    if (bookingData) {
      const price = bookingData.grandTotal || bookingData.grand_total;
      widgetData.price = thn.parseNumber(price);
      widgetData.currency = document.querySelector('.details-wrapper-advance')?.textContent?.match(/[A-Z]{3}/)?.[0];
      widgetData.bookingId = bookingData.bookingId;
      widgetData.dateBegin = bookingData.checkIn.replace(/-/g, '/');
      widgetData.dateEnd = bookingData.checkOut.replace(/-/g, '/');
    } else {
      const priceEl = document.querySelector('#collapseFour .booking-payment-block .gt-amt') || document.querySelector('#collapseFour .booking-payment-block .bl-amt');
      widgetData.price = thn.parseNumber(priceEl?.textContent);
      widgetData.currency = thn.parseCurrency(priceEl?.textContent);
      widgetData.bookingId = document.querySelector('#collapseFour > div > div > div:nth-child(2) .details-wrapper.text-right p')?.textContent;
      widgetData.dateBegin = translateDate(document.querySelector('#checkInWrapper span')?.textContent?.replace('\'', ''), thn.data.language);
      widgetData.dateEnd = translateDate(document.querySelector('#checkOutWrapper span')?.textContent?.replace('\'', ''), thn.data.language);
    }

    return widgetData;
  };

  return engine;
}

export { factory };
